"use client";

import { useQuery } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks/shared";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { UserTasksApi, type UserTaskStatus } from "@/openapi/generated";

const DEFAULT_PAGE_SIZE = 50;

export function useUserTasksGet(options?: {
  activeOnly?: boolean;
  status?: UserTaskStatus | null;
  overdueOnly?: boolean;
  enabled?: boolean;
  page?: number;
  pageSize?: number;
}) {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const status = options?.status ?? null;
  const overdueOnly = options?.overdueOnly ?? false;
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const enabled = options?.enabled ?? true;
  const useActiveFilter =
    options?.activeOnly === true && !overdueOnly && status == null;

  return useQuery({
    queryKey: [
      QueryKeys.UserTasks,
      tenantId,
      useActiveFilter,
      status,
      overdueOnly,
      page,
      pageSize,
    ],
    enabled:
      enabled && !!accessToken && tenantId != null && tenantId > 0,
    queryFn: async () => {
      const token = accessToken;
      const tid = tenantId;
      if (!token || tid == null) {
        throw new Error("Missing session or tenant for tasks");
      }
      const service = createAuthenticatedApi(UserTasksApi, token);
      return service.apiUserTasksGet({
        xTenantID: tid,
        page,
        pageSize,
        active: useActiveFilter ? true : undefined,
        status: status ?? undefined,
        overdue: overdueOnly ? true : undefined,
      });
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}
